import { useEffect, useRef, useState } from 'react';
import { Device } from '@twilio/voice-sdk';
import Modal from '../components/Modal';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const TWILIO_TOKEN_URL = process.env.REACT_APP_TWILIO_TOKEN_URL || `${API_BASE}/backend/twilio_token_proxy.php`;

function DialerPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState('');
  const [modeStatus, setModeStatus] = useState('fresh');
  const [sessionId, setSessionId] = useState(null);
  const [session, setSession] = useState(null);
  const [leadsAll, setLeadsAll] = useState([]);
  const [leadIds, setLeadIds] = useState([]);
  const [queuedLeads, setQueuedLeads] = useState([]);
  const [currentLead, setCurrentLead] = useState(null);
  const [status, setStatus] = useState('');
  const deviceRef = useRef(null);
  const [deviceReady, setDeviceReady] = useState(false);
  const callRef = useRef(null);
  const [detailLead, setDetailLead] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [dialMode, setDialMode] = useState('auto');
  const [startOpen, setStartOpen] = useState(false);
  const sessionIdRef = useRef(null);
  const dialModeRef = useRef('auto');
  const currentLeadRef = useRef(null);
  const autoAdvanceRef = useRef(false);

  const fetchCampaigns = async () => {
    const res = await fetch(`${API_BASE}/backend/campaigns_list.php`);
    const data = await res.json();
    if (data.ok) setCampaigns(data.campaigns);
  };

  const fetchSession = async (sid) => {
    if (!sid) return;
    const res = await fetch(`${API_BASE}/backend/dialer_session.php?session_id=${sid}`);
    const data = await res.json();
    if (data.ok) setSession(data.session);
  };

  const fetchLeadsAll = async (cid) => {
    if (!cid) return;
    const res = await fetch(`${API_BASE}/backend/leads_list.php?campaign_id=${cid}`);
    const data = await res.json();
    if (data.ok) setLeadsAll(data.leads);
  };

  const fetchQueuedLeads = async (ids) => {
    if (!ids || ids.length === 0) {
      setQueuedLeads([]);
      return;
    }
    const res = await fetch(`${API_BASE}/backend/leads_by_ids.php?ids=${ids.join(',')}`);
    const data = await res.json();
    if (data.ok) setQueuedLeads(data.leads);
  };

  const fetchCurrentLead = async () => {
    if (!campaignId) return;
    const res = await fetch(`${API_BASE}/backend/dialer_current.php?campaign_id=${campaignId}`);
    const data = await res.json();
    if (data.ok) setCurrentLead(data.lead || null);
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    const t = setInterval(() => {
      fetchSession(sessionId);
      fetchCurrentLead();
      fetchLeadsAll(campaignId);
    }, 4000);
    return () => clearInterval(t);
  }, [sessionId]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    dialModeRef.current = dialMode;
  }, [dialMode]);

  useEffect(() => {
    currentLeadRef.current = currentLead;
  }, [currentLead]);

  useEffect(() => {
    if (campaignId) fetchLeadsAll(campaignId);
  }, [campaignId]);

  useEffect(() => {
    if (!sessionId || dialModeRef.current !== 'auto') return;
    const poll = setInterval(async () => {
      const lead = currentLeadRef.current;
      if (!lead) {
        if (!autoAdvanceRef.current) {
          autoAdvanceRef.current = true;
          setStatus('Auto‑advancing to next lead...');
          await fetchNext();
          autoAdvanceRef.current = false;
        }
        return;
      }
      const res = await fetch(`${API_BASE}/backend/lead_view.php?id=${lead.id}`);
      const data = await res.json();
      if (data.ok) {
        const st = data.lead.status;
        if (st && st !== 'calling') {
          // Callee ended or call completed without client disconnect event
          callRef.current = null;
          currentLeadRef.current = null;
          setCurrentLead(null);
          if (!autoAdvanceRef.current) {
            autoAdvanceRef.current = true;
            setStatus('Auto‑advancing to next lead...');
            await fetchNext();
            autoAdvanceRef.current = false;
          }
        }
      }
    }, 5000);
    return () => clearInterval(poll);
  }, [sessionId]);

  const startDialer = async () => {
    if (!campaignId) {
      setStatus('Select a campaign first.');
      return;
    }

    try {
      await ensureDeviceReady();
    } catch (err) {
      setStatus('Twilio device failed to register.');
      return;
    }

    const form = new FormData();
    form.append('campaign_id', campaignId);
    form.append('mode_status', modeStatus);

    const res = await fetch(`${API_BASE}/backend/dialer_start.php`, { method: 'POST', body: form });
    const data = await res.json();
    if (data.ok) {
      setSessionId(data.session_id);
      setLeadIds(data.lead_ids || []);
      fetchQueuedLeads(data.lead_ids || []);
      fetchCurrentLead();
      setStatus('Dialer running. Dialing next lead...');
      fetchSession(data.session_id);
      if (data.lead_ids && data.lead_ids[0]) {
        await dialLead(data.lead_ids[0]);
      }
    } else {
      setStatus(data.error || 'Failed to start dialer.');
    }
  };

  const dialLead = async (leadId) => {
    if (!deviceRef.current) return;
    if (callRef.current) return;
    try {
      const lead = queuedLeads.find((l) => String(l.id) === String(leadId));
      if (lead) setCurrentLead(lead);
      const call = await deviceRef.current.connect({ params: { lead_id: String(leadId) } });
      callRef.current = call;
      call.on('disconnect', async () => {
        callRef.current = null;
        if (dialModeRef.current === 'auto' && sessionIdRef.current) {
          await fetchNext();
        }
      });
      call.on('error', async () => {
        callRef.current = null;
        if (dialModeRef.current === 'auto' && sessionIdRef.current) {
          await fetchNext();
        }
      });
    } catch (err) {
      setStatus('Call failed to start.');
    }
  };

  const endCall = () => {
    if (callRef.current) {
      callRef.current.disconnect();
      callRef.current = null;
      if (dialModeRef.current === 'auto' && sessionIdRef.current) {
        fetchNext();
      }
    }
  };

  const pauseDialer = async () => {
    if (!sessionId) return;
    const form = new FormData();
    form.append('session_id', sessionId);
    const res = await fetch(`${API_BASE}/backend/dialer_pause.php`, { method: 'POST', body: form });
    const data = await res.json();
    if (data.ok) setStatus('Dialer paused.');
  };

  const stopDialer = async () => {
    if (!sessionId) return;
    const form = new FormData();
    form.append('session_id', sessionId);
    const res = await fetch(`${API_BASE}/backend/dialer_stop.php`, { method: 'POST', body: form });
    const data = await res.json();
    if (data.ok) setStatus('Dialer stopped.');
  };

  const fetchNext = async () => {
    if (!sessionId) return;
    if (callRef.current) {
      setStatus('Call in progress. End call before fetching next.');
      return;
    }
    const form = new FormData();
    form.append('session_id', sessionId);
    const res = await fetch(`${API_BASE}/backend/dialer_next.php`, { method: 'POST', body: form });
    const data = await res.json();
    if (data.ok) {
      setLeadIds(data.lead_ids || []);
      fetchQueuedLeads(data.lead_ids || []);
      fetchCurrentLead();
      if (data.lead_ids.length) {
        setStatus(dialMode === 'auto' ? 'Dialing next lead...' : 'Ready to dial next lead.');
        await dialLead(data.lead_ids[0]);
      } else {
        setStatus('No eligible leads found.');
      }
    }
  };

  const callNow = async (lead) => {
    if (!lead) return;
    await ensureDeviceReady();
    setCurrentLead(lead);
    await dialLead(lead.id);
  };

  const ensureDeviceReady = async () => {
    const tokenRes = await fetch(TWILIO_TOKEN_URL);
    const tokenData = await tokenRes.json();
    const tokenValue = tokenData.token;
    if (!tokenValue) {
      setStatus(tokenData.error || 'Failed to get Twilio token.');
      throw new Error('token');
    }
    if (!deviceRef.current) {
      const device = new Device(tokenValue, { logLevel: 1 });
      device.on('registered', () => setDeviceReady(true));
      device.on('error', (e) => setStatus(`Twilio device error: ${e.message || 'unknown'}`));
      deviceRef.current = device;
      await device.register();
    } else {
      deviceRef.current.updateToken(tokenValue);
      await deviceRef.current.register();
    }
  };

  const sessionStatus = session?.status || 'stopped';
  const isRunning = sessionStatus === 'running';
  const isPaused = sessionStatus === 'paused';
  const canManualCall = sessionStatus === 'paused' || sessionStatus === 'stopped';

  const eligibleStatuses = modeStatus === 'redial'
    ? ['voicemail', 'no_answer', 'busy', 'failed']
    : ['new', 'call_back'];
  const remaining = leadsAll.filter((l) =>
    eligibleStatuses.includes(l.status) &&
    Number(l.dnc) === 0 &&
    Number(l.consent) === 1 &&
    Number(l.call_attempts || 0) < 1
  ).length;

  return (
    <div className="grid">
      <section className="card">
        <h2>Power Dialer</h2>
        <div className="status-box">
          <div>
            <div className="muted">Call Status</div>
            <div className="status-title">{isRunning ? 'Calling' : isPaused ? 'Paused' : 'Stopped'}</div>
            {currentLead && (
              <div className="muted">Now calling: {currentLead.name || '-'} ({currentLead.phone || '-'})</div>
            )}
          </div>
          <button className="danger" onClick={endCall} disabled={!callRef.current}>End Call</button>
        </div>
        <div className="row">
          <label>
            Campaign
            <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
              <option value="">Select campaign</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <label>
            Target Group
            <select value={modeStatus} onChange={(e) => setModeStatus(e.target.value)}>
              <option value="fresh">Fresh (new/call_back)</option>
              <option value="redial">Re-dial (voicemail/no_answer/busy/failed)</option>
            </select>
          </label>
        </div>
        {(isRunning || isPaused) && (
          <div className="muted">Press Stop or Pause Dialer</div>
        )}
        <div className="row actions">
          <button className="primary" onClick={() => setStartOpen(true)} disabled={isRunning || isPaused}>Start</button>
          <button onClick={pauseDialer} disabled={!isRunning}>Pause</button>
          <button className="danger" onClick={stopDialer} disabled={!isRunning && !isPaused}>Stop</button>
          <button className="button" onClick={fetchNext} disabled={dialMode !== 'manual'}>Fetch Next</button>
        </div>
        {!deviceReady && <div className="muted">Twilio device not connected yet.</div>}
        {status && <div className="status">{status}</div>}
      </section>

      <section className="card">
        <div className="row spread">
          <div>
            <h3>Leads</h3>
            <div className="muted">Remaining {remaining} of {leadsAll.length}</div>
          </div>
        </div>
        <div className="table scrollable">
          <div className="table-row head">
            <div>Name</div>
            <div>Phone</div>
            <div>Status</div>
            <div>Attempts</div>
            <div>Last Called</div>
            <div>Actions</div>
          </div>
          {leadsAll.map((l) => (
            <div
              key={l.id}
              className={`table-row ${currentLead?.id === l.id ? 'active' : ''}`}
            >
              <div>{l.name || '-'}</div>
              <div>{l.phone || '-'}</div>
              <div>{l.status || '-'}</div>
              <div>{l.call_attempts || 0}</div>
              <div>{l.last_called_at || 'Not called yet'}</div>
              <div className="row">
                <button className="button" onClick={() => { setDetailLead(l); setDetailOpen(true); }}>View</button>
                <button
                  className="primary"
                  onClick={() => callNow(l)}
                  disabled={!canManualCall}
                >
                  Call Now
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      

      {detailOpen && detailLead && (
        <aside className="sidepanel overlay open">
          <div className="sidepanel-header">
            <h3>Lead Details</h3>
            <button className="button ghost" onClick={() => setDetailOpen(false)}>Close</button>
          </div>
          <div className="sidepanel-body">
            <div className="detail-grid">
              <div><span>Name</span>{detailLead.name || '-'}</div>
              <div><span>Phone</span>{detailLead.phone || '-'}</div>
              <div><span>Status</span>{detailLead.status || '-'}</div>
              <div><span>Attempts</span>{detailLead.call_attempts || 0}</div>
              <div><span>Last Called</span>{detailLead.last_called_at || 'Not called yet'}</div>
            </div>
            <div className="row spread">
              <span className="muted">{currentLead ? `Calling: ${currentLead.name || '-'} (${currentLead.phone || '-'})` : 'No active call'}</span>
              <button className="danger" onClick={endCall} disabled={!callRef.current}>End Call</button>
            </div>
          </div>
        </aside>
      )}

      {startOpen && (
        <Modal title="Start Dialer" onClose={() => setStartOpen(false)}>
          <div className="muted">Choose how you want to dial.</div>
          <div className="row">
            <button
              className="primary"
              onClick={async () => {
                setDialMode('auto');
                setStartOpen(false);
                await startDialer();
              }}
            >
              Auto‑Dial (Recommended)
            </button>
            <button
              className="button"
              onClick={async () => {
                setDialMode('manual');
                setStartOpen(false);
                await startDialer();
              }}
            >
              Manual (Use Fetch Next)
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default DialerPage;
